import React, { useEffect, useMemo, useState } from "react"
import { listAdminPayouts } from "../../services/wallet"
import { Link } from "react-router-dom"
import {
  ArrowRightOnRectangleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline"

const AdminPayouts = () => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [query, setQuery] = useState("")

  const fetchItems = async () => {
    setLoading(true)
    setError("")
    try {
      const res = await listAdminPayouts()
      setLoading(false)
      if (res.status === 200) {
        setItems(Array.isArray(res.data) ? res.data : [])
      } else {
        setError(res.error || 'Failed to load payouts')
      }
    } catch (err) {
      setLoading(false)
      setError('An unexpected error occurred')
    }
  }

  useEffect(() => { fetchItems() }, [])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items
    return items.filter(i => (
      (i.centerName || '').toLowerCase().includes(q) ||
      (i.reference || '').toLowerCase().includes(q)
    ))
  }, [items, query])

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Payouts</h1>
        <p className="text-slate-500 mt-1">Manage withdrawal requests</p>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between items-center">
        <div className="relative w-full max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by center or ref..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent bg-white shadow-sm"
          />
        </div>
        <button onClick={fetchItems} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition">
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
          <p className="mt-2 text-slate-500">Loading payouts...</p>
        </div>
      ) : error ? (
        <div className="p-4 rounded-xl bg-red-50 text-red-600 border border-red-100 text-center">
          {error}
          <button onClick={fetchItems} className="block mx-auto mt-2 text-sm font-bold hover:underline">Try Again</button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 font-semibold text-slate-700">Reference</th>
                  <th className="px-6 py-4 font-semibold text-slate-700">Requested By</th>
                  <th className="px-6 py-4 font-semibold text-slate-700">Amount</th>
                  <th className="px-6 py-4 font-semibold text-slate-700">Date</th>
                  <th className="px-6 py-4 font-semibold text-slate-700">Status</th>
                  <th className="px-6 py-4 font-semibold text-slate-700 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                      No payouts found.
                    </td>
                  </tr>
                ) : (
                  filtered.map(item => (
                    <tr key={item._id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 font-mono text-slate-600">
                        {item.reference || item._id.slice(-6)}
                      </td>
                      <td className="px-6 py-4 text-slate-900 font-medium">
                        {item.centerName || item.userId?.name || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 text-slate-900 font-bold">
                        ₦{item.amount?.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${item.status === 'COMPLETED' ? 'bg-green-50 text-green-700 border-green-200' :
                            item.status === 'PENDING' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                              'bg-red-50 text-red-700 border-red-200'
                          }`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Link
                          to={`/admin/payouts/${item._id}`}
                          className="text-cyan-600 hover:text-cyan-800 font-medium hover:underline"
                        >
                          Review
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminPayouts
